/**
 * Browser viewport contract — the sizes a preview surface can be asked to be.
 *
 * Split from `browser.ts` because `BrowserViewport` there is a *measurement*
 * (whatever size the guest currently reports, unconstrained) while everything
 * here is a *request* (what a user or an agent asked for, which has to be
 * validated). Conflating the two is how an agent's `resize` ends up accepting a
 * 0x0 or a 16000x16000 viewport that the guest then silently clamps.
 *
 * Ported from t3code (`packages/contracts/src/preview.ts`,
 * `packages/shared/src/previewViewport.ts`), renamed onto Forge's `Browser*`
 * vocabulary. The device catalog and its dimensions are Chrome DevTools'
 * standard emulated-device list, kept in DevTools' own order so the picker reads
 * the way users expect.
 *
 * @module browserViewport
 */
import * as Schema from "effect/Schema";

/**
 * Bounds for a *requested* viewport.
 *
 * The floor exists because a guest below it cannot lay out real content, and the
 * area cap because width x height is what allocates the framebuffer — two
 * in-range dimensions can still multiply into an allocation that kills the
 * runtime, so the area is checked separately rather than implied.
 */
export const BROWSER_VIEWPORT_MIN_DIMENSION = 240;
export const BROWSER_VIEWPORT_MAX_DIMENSION = 3840;
export const BROWSER_VIEWPORT_MAX_AREA = 3840 * 2160;

const BrowserViewportDimension = Schema.Int.check(
  Schema.isBetween({
    minimum: BROWSER_VIEWPORT_MIN_DIMENSION,
    maximum: BROWSER_VIEWPORT_MAX_DIMENSION,
  }),
);

const viewportAreaFilter = Schema.makeFilter(
  ({ width, height }: { readonly width: number; readonly height: number }) =>
    width * height <= BROWSER_VIEWPORT_MAX_AREA ||
    `Viewport area must not exceed ${BROWSER_VIEWPORT_MAX_AREA} pixels.`,
);

/** A user- or agent-selectable viewport size. */
export const BrowserViewportSize = Schema.Struct({
  width: BrowserViewportDimension,
  height: BrowserViewportDimension,
}).check(viewportAreaFilter);
export type BrowserViewportSize = typeof BrowserViewportSize.Type;

/**
 * A *measured* viewport, which may legitimately be smaller than the minimum
 * selectable size: fill mode follows the panel, and a narrow panel is narrower
 * than anything a user could pick. Validating a measurement against the
 * selection bounds would reject reality.
 */
export const BrowserRenderedViewportSize = Schema.Struct({
  width: Schema.Int.check(Schema.isGreaterThan(0)),
  height: Schema.Int.check(Schema.isGreaterThan(0)),
});
export type BrowserRenderedViewportSize = typeof BrowserRenderedViewportSize.Type;

/** Chrome DevTools' standard emulated devices, in DevTools' order. */
export const BROWSER_VIEWPORT_PRESET_IDS = [
  "iphone-se",
  "iphone-xr",
  "iphone-12-pro",
  "iphone-14-pro-max",
  "pixel-7",
  "samsung-galaxy-s8-plus",
  "samsung-galaxy-s20-ultra",
  "ipad-mini",
  "ipad-air",
  "ipad-pro",
  "surface-pro-7",
  "surface-duo",
  "galaxy-z-fold-5",
  "asus-zenbook-fold",
  "samsung-galaxy-a51-71",
  "nest-hub",
  "nest-hub-max",
] as const;

export const BrowserViewportPresetId = Schema.Literals(BROWSER_VIEWPORT_PRESET_IDS);
export type BrowserViewportPresetId = typeof BrowserViewportPresetId.Type;

export type BrowserViewportPresetCategory = "Desktop" | "Tablet" | "Phone";

export interface BrowserViewportPreset {
  readonly id: BrowserViewportPresetId;
  readonly label: string;
  readonly category: BrowserViewportPresetCategory;
  /** Human-readable dimensions for the picker row, e.g. `375 × 667`. */
  readonly detail: string;
  readonly width: number;
  readonly height: number;
}

const BROWSER_VIEWPORT_PRESET_DEFINITIONS: Record<
  BrowserViewportPresetId,
  Omit<BrowserViewportPreset, "id">
> = {
  "iphone-se": {
    label: "iPhone SE",
    category: "Phone",
    detail: "375 × 667",
    width: 375,
    height: 667,
  },
  "iphone-xr": {
    label: "iPhone XR",
    category: "Phone",
    detail: "414 × 896",
    width: 414,
    height: 896,
  },
  "iphone-12-pro": {
    label: "iPhone 12 Pro",
    category: "Phone",
    detail: "390 × 844",
    width: 390,
    height: 844,
  },
  "iphone-14-pro-max": {
    label: "iPhone 14 Pro Max",
    category: "Phone",
    detail: "430 × 932",
    width: 430,
    height: 932,
  },
  "pixel-7": { label: "Pixel 7", category: "Phone", detail: "412 × 915", width: 412, height: 915 },
  "samsung-galaxy-s8-plus": {
    label: "Samsung Galaxy S8+",
    category: "Phone",
    detail: "360 × 740",
    width: 360,
    height: 740,
  },
  "samsung-galaxy-s20-ultra": {
    label: "Samsung Galaxy S20 Ultra",
    category: "Phone",
    detail: "412 × 915",
    width: 412,
    height: 915,
  },
  "ipad-mini": {
    label: "iPad Mini",
    category: "Tablet",
    detail: "768 × 1024",
    width: 768,
    height: 1024,
  },
  "ipad-air": {
    label: "iPad Air",
    category: "Tablet",
    detail: "820 × 1180",
    width: 820,
    height: 1180,
  },
  "ipad-pro": {
    label: "iPad Pro",
    category: "Tablet",
    detail: "1024 × 1366",
    width: 1024,
    height: 1366,
  },
  "surface-pro-7": {
    label: "Surface Pro 7",
    category: "Tablet",
    detail: "912 × 1368",
    width: 912,
    height: 1368,
  },
  "surface-duo": {
    label: "Surface Duo",
    category: "Phone",
    detail: "540 × 720",
    width: 540,
    height: 720,
  },
  "galaxy-z-fold-5": {
    label: "Galaxy Z Fold 5",
    category: "Phone",
    detail: "344 × 882",
    width: 344,
    height: 882,
  },
  "asus-zenbook-fold": {
    label: "Asus Zenbook Fold",
    category: "Tablet",
    detail: "853 × 1280",
    width: 853,
    height: 1280,
  },
  "samsung-galaxy-a51-71": {
    label: "Samsung Galaxy A51/71",
    category: "Phone",
    detail: "412 × 914",
    width: 412,
    height: 914,
  },
  "nest-hub": {
    label: "Nest Hub",
    category: "Tablet",
    detail: "1024 × 600",
    width: 1024,
    height: 600,
  },
  "nest-hub-max": {
    label: "Nest Hub Max",
    category: "Tablet",
    detail: "1280 × 800",
    width: 1280,
    height: 800,
  },
};

export const BROWSER_VIEWPORT_PRESETS: readonly BrowserViewportPreset[] =
  BROWSER_VIEWPORT_PRESET_IDS.map((id) => ({ id, ...BROWSER_VIEWPORT_PRESET_DEFINITIONS[id] }));

export function browserViewportPreset(id: BrowserViewportPresetId): BrowserViewportPreset {
  return { id, ...BROWSER_VIEWPORT_PRESET_DEFINITIONS[id] };
}

/**
 * Preset IDs that shipped before the Chrome-compatible catalog.
 *
 * Accepted when *reading* a stored setting so an existing session still
 * reconnects, but never offered for a new selection. Dropping them outright
 * would make a saved viewport fail validation and take the whole session's
 * persisted state down with it.
 */
export const LEGACY_BROWSER_VIEWPORT_PRESET_IDS = [
  "desktop-1920x1080",
  "desktop-1440x900",
  "laptop-1366x768",
  "laptop-1280x800",
  "ipad-pro-11",
  "iphone-15-pro",
  "pixel-8",
  "galaxy-s24",
] as const;

const StoredBrowserViewportPresetId = Schema.Literals([
  ...BROWSER_VIEWPORT_PRESET_IDS,
  ...LEGACY_BROWSER_VIEWPORT_PRESET_IDS,
]);

/**
 * What viewport a surface should present at.
 *
 * `fill` follows the panel and is the default; `freeform` is a dragged or typed
 * size; `preset` is a device from the catalog. A preset carries its own width
 * and height rather than only its id, so a stored setting still resolves to the
 * size the user saw even if the catalog's dimensions are later corrected.
 */
export const BrowserViewportSetting = Schema.Union([
  Schema.TaggedStruct("fill", {}),
  Schema.TaggedStruct("freeform", { ...BrowserViewportSize.fields }).check(viewportAreaFilter),
  Schema.TaggedStruct("preset", {
    ...BrowserViewportSize.fields,
    presetId: StoredBrowserViewportPresetId,
  }).check(viewportAreaFilter),
]);
export type BrowserViewportSetting = typeof BrowserViewportSetting.Type;

export const FILL_BROWSER_VIEWPORT = { _tag: "fill" } as const satisfies BrowserViewportSetting;

/**
 * Chrome's zoom ladder.
 *
 * Zoom is applied to the guest by whichever backend owns it, but the steps live
 * here so the surface's zoom controls and the settings UI step through exactly
 * the same values.
 */
export const BROWSER_ZOOM_LEVELS = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5,
] as const;

export const BrowserZoomFactor = Schema.Literals(BROWSER_ZOOM_LEVELS);
export type BrowserZoomFactor = typeof BrowserZoomFactor.Type;

export const DEFAULT_BROWSER_ZOOM_FACTOR: BrowserZoomFactor = 1;

/**
 * Preferred `prefers-color-scheme` for a browser guest. `system` clears the
 * emulation override so the guest follows the OS rather than pinning light.
 */
export const BrowserAppearancePreference = Schema.Literals(["system", "light", "dark"]);
export type BrowserAppearancePreference = typeof BrowserAppearancePreference.Type;

export const DEFAULT_BROWSER_APPEARANCE: BrowserAppearancePreference = "system";

/** Resolve a setting to concrete pixels, given the panel to fill. */
export function resolveBrowserViewportSize(
  setting: BrowserViewportSetting,
  panel: { readonly width: number; readonly height: number },
): { readonly width: number; readonly height: number } {
  return setting._tag === "fill" ? panel : { width: setting.width, height: setting.height };
}
